import React from 'react';
import { 
  FaCheckCircle, 
  FaDownload, 
  FaShareAlt, 
  FaCalendarAlt,
  FaUserFriends,
  FaTicketAlt,
  FaMapMarkerAlt,
  FaClock
} from 'react-icons/fa';

const BookingComplete = ({
  selectedActivity,
  selectedOption,
  selectedDate,
  selectedTime,
  visitors,
  priceBreakdown,
  selectedCountry,
  resetBookingFlow
}) => {
  const totalVisitors = visitors.reduce((sum, v) => sum + v.count, 0);
  const bookingId = `BK-${Math.random().toString(36).substr(2, 8).toUpperCase()}`;

  return (
    <div className="text-center py-12">
      <div className="relative inline-block mb-8">
        <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full blur-xl opacity-30"></div>
        <div className="relative bg-gradient-to-r from-green-500 to-emerald-600 w-24 h-24 rounded-full flex items-center justify-center">
          <FaCheckCircle className="text-white text-5xl" />
        </div>
      </div>

      <h2 className="text-4xl font-bold text-gray-900 mb-4">
        Booking Confirmed! 🎉
      </h2>
      <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
        Your adventure is booked! We've sent a confirmation email with all the details.
        Get ready for an amazing experience!
      </p>

      <div className="bg-white rounded-2xl shadow-xl border max-w-4xl mx-auto mb-12 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-2">Booking #{bookingId}</h3>
              <p className="text-blue-100">Status: <span className="font-semibold">Confirmed</span></p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">${priceBreakdown?.total?.toFixed(2) || '0.00'}</div>
              <div className="text-blue-200">Amount Paid</div>
            </div>
          </div>
        </div>

        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-gray-50 rounded-xl p-5">
              <div className="flex items-center mb-3">
                <div className="p-2 bg-blue-100 rounded-lg mr-3">
                  <FaMapMarkerAlt className="text-blue-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-600">Destination</div>
                  <div className="font-bold text-gray-900">{selectedCountry?.name}</div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-5">
              <div className="flex items-center mb-3">
                <div className="p-2 bg-green-100 rounded-lg mr-3">
                  <FaTicketAlt className="text-green-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-600">Activity</div>
                  <div className="font-bold text-gray-900">{selectedActivity?.name}</div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-5">
              <div className="flex items-center mb-3">
                <div className="p-2 bg-purple-100 rounded-lg mr-3">
                  <FaCalendarAlt className="text-purple-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-600">Date & Time</div>
                  <div className="font-bold text-gray-900">
                    {selectedDate} at {selectedTime}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-5">
              <div className="flex items-center mb-3">
                <div className="p-2 bg-orange-100 rounded-lg mr-3">
                  <FaUserFriends className="text-orange-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-600">Guests</div>
                  <div className="font-bold text-gray-900">{totalVisitors}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t pt-8">
            <h4 className="text-lg font-bold text-gray-900 mb-4">Important Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div className="bg-blue-50 rounded-xl p-4">
                <div className="flex items-center mb-2">
                  <FaClock className="text-blue-600 mr-2" />
                  <span className="font-medium">Check-in Time</span>
                </div>
                <p className="text-sm text-gray-600">
                  Please arrive 30 minutes before the scheduled time at the activity location.
                </p>
              </div>
              
              <div className="bg-green-50 rounded-xl p-4">
                <div className="flex items-center mb-2">
                  <FaCheckCircle className="text-green-600 mr-2" />
                  <span className="font-medium">What to Bring</span>
                </div>
                <p className="text-sm text-gray-600">
                  Valid ID, comfortable clothing, and your booking confirmation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={resetBookingFlow}
          className="px-8 py-3.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold hover:shadow-lg transition-all duration-300 flex items-center justify-center"
        >
          Book Another Activity
        </button>
        
        <button className="px-8 py-3.5 border-2 border-blue-500 text-blue-600 rounded-xl font-bold hover:bg-blue-50 transition-colors flex items-center justify-center">
          <FaDownload className="mr-2" />
          Download Receipt
        </button>
        
        <button className="px-8 py-3.5 border-2 border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-50 transition-colors flex items-center justify-center">
          <FaShareAlt className="mr-2" />
          Share Booking
        </button>
      </div>

      <div className="mt-12 text-sm text-gray-500">
        <p>Confirmation email sent to your registered email address.</p>
        <p className="mt-1">Need help? Contact support@adventurebook.com</p>
      </div>
    </div>
  );
};

export default BookingComplete;